import { useEffect, useRef, useState } from 'react'
import { getActorMovies, getMovieCast, getMovieCastFull, posterUrl, profileUrl } from '../../api/tmdb'
import { updateParty } from '../../utils/partyGame'
import Filmstrip from '../Filmstrip'
import PartyDCScoreboard from './PartyDCScoreboard'
import { DCDirectorBanner, DCHostDirectorLine } from './PartyDCIdentity'
import { IconCheck, IconX } from '../icons'

const MOVIE_PH = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='342' height='513' viewBox='0 0 342 513'%3E%3Crect fill='%23333' width='342' height='513'/%3E%3C/svg%3E"
const ACTOR_PH = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='185' height='278' viewBox='0 0 185 278'%3E%3Crect fill='%23333' width='185' height='278'/%3E%3Ccircle fill='%23555' cx='92' cy='100' r='40'/%3E%3Cellipse fill='%23555' cx='92' cy='220' rx='60' ry='50'/%3E%3C/svg%3E"

/**
 * Director's Cut — Phase 3: Gameplay loop.
 *
 * dcStep substatus values:
 *   'movie-nominate' : agents pitch movies, director sees submission status
 *   'movie-pick'     : director picks one movie anonymously, +1 to nominator(s)
 *   'movie-score'    : 4-second scoreboard interlude, then → 'actor-nominate'
 *   'actor-nominate' : agents pitch actors from the chosen movie
 *   'actor-pick'     : director picks one actor anonymously, +1 to nominator(s)
 *   'actor-score'    : 4-second scoreboard interlude. If picked = goal → status: 'dc-results'.
 *
 * role: 'host' | 'director' | 'agent'
 *
 * TODO: the points system here could be extracted and extended to other modes
 * in the future (e.g. an MVP scoring layer for Co-Op, or H2H individual scoring).
 */
export default function PartyDCGameplay({ party, currentUserId, role }) {
  const players      = party?.players ?? {}
  const directorUid  = party?.directorUid
  const goalActor    = party?.goalActor
  const startActor   = party?.startActor
  const dcChain      = Array.isArray(party?.dcChain) ? party.dcChain : []
  const dcStep       = party?.dcStep || 'movie-nominate'
  const dcNominations = party?.dcNominations ?? {}
  const agentUids    = Object.keys(players).filter(uid => uid !== directorUid)
  const submittedUids = Object.keys(dcNominations)
  const allSubmitted = agentUids.length > 0 && submittedUids.length >= agentUids.length

  const isHost     = role === 'host'
  const isDirector = role === 'director'
  const isAgent    = role === 'agent'

  // Current actor in play (last actor in chain, or startActor if chain empty)
  const currentActor = [...dcChain].reverse().find((c) => c.type === 'actor') || startActor
  const currentMovie = [...dcChain].reverse().find((c) => c.type === 'movie')

  const usedActorIds = new Set(dcChain.filter(c => c.type === 'actor').map(c => c.id))
  const usedMovieIds = new Set(dcChain.filter(c => c.type === 'movie').map(c => c.id))

  // ── Host auto-advance: when all agents submitted, advance to pick phase ──
  const advancedRef = useRef(false)
  useEffect(() => { advancedRef.current = false }, [dcStep])
  useEffect(() => {
    if (!isHost || advancedRef.current || !party?.id) return
    if (dcStep === 'movie-nominate' && allSubmitted) {
      advancedRef.current = true
      updateParty(party.id, { dcStep: 'movie-pick', dcStepStartedAt: new Date() }).catch(() => {})
    } else if (dcStep === 'actor-nominate' && allSubmitted) {
      advancedRef.current = true
      updateParty(party.id, { dcStep: 'actor-pick', dcStepStartedAt: new Date() }).catch(() => {})
    }
  }, [isHost, dcStep, allSubmitted, party?.id])

  // ── SCOREBOARD interlude steps ────────────────────────────────────────────
  if (dcStep === 'movie-score') {
    return (
      <PartyDCScoreboard
        party={party}
        currentUserId={currentUserId}
        role={role}
        pickedLabel={party?.dcLastPickedLabel}
        scoredUids={party?.dcLastScoredUids ?? []}
        nextStep="actor-nominate"
      />
    )
  }
  if (dcStep === 'actor-score') {
    // If the picked actor was the goal, status flips to dc-results instead
    const lastActor = [...dcChain].reverse().find((c) => c.type === 'actor')
    const reachedGoal = lastActor && goalActor && lastActor.id === goalActor.id
    return (
      <PartyDCScoreboard
        party={party}
        currentUserId={currentUserId}
        role={role}
        pickedLabel={party?.dcLastPickedLabel}
        scoredUids={party?.dcLastScoredUids ?? []}
        nextStep={reachedGoal ? 'movie-nominate' : 'movie-nominate'}
        nextStatus={reachedGoal ? 'dc-results' : undefined}
      />
    )
  }

  // ── MOVIE NOMINATE ────────────────────────────────────────────────────────
  if (dcStep === 'movie-nominate') {
    if (isAgent) return (
      <MovieNominate
        party={party}
        currentUserId={currentUserId}
        currentActor={currentActor}
        usedMovieIds={usedMovieIds}
        dcNominations={dcNominations}
      />
    )
    return (
      <WaitingForSubmissions
        party={party}
        role={role}
        agentUids={agentUids}
        nominations={dcNominations}
        title={isDirector ? 'Agents are pitching films...' : 'Casting the next scene'}
      />
    )
  }

  // ── MOVIE PICK ────────────────────────────────────────────────────────────
  if (dcStep === 'movie-pick') {
    return (
      <DirectorPick
        party={party}
        role={role}
        kind="movie"
        nominations={dcNominations}
        currentActor={currentActor}
      />
    )
  }

  // ── ACTOR NOMINATE ────────────────────────────────────────────────────────
  if (dcStep === 'actor-nominate') {
    if (isAgent) return (
      <ActorNominate
        party={party}
        currentUserId={currentUserId}
        currentMovie={currentMovie}
        goalActor={goalActor}
        usedActorIds={usedActorIds}
        currentActor={currentActor}
        dcNominations={dcNominations}
      />
    )
    return (
      <WaitingForSubmissions
        party={party}
        role={role}
        agentUids={agentUids}
        nominations={dcNominations}
        title={isDirector ? 'Agents are pitching actors...' : 'Casting the role'}
      />
    )
  }

  // ── ACTOR PICK ────────────────────────────────────────────────────────────
  if (dcStep === 'actor-pick') {
    return (
      <DirectorPick
        party={party}
        role={role}
        kind="actor"
        nominations={dcNominations}
        currentActor={currentActor}
        goalActor={goalActor}
      />
    )
  }

  // Fallback
  return (
    <div className="dc-gameplay">
      <p className="dc-gameplay__pulse">Loading scene...</p>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// MovieNominate — agent picks a movie from the current actor's filmography
// ────────────────────────────────────────────────────────────────────────────
function MovieNominate({ party, currentUserId, currentActor, usedMovieIds, dcNominations }) {
  const [movies, setMovies]   = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState('')
  const [movieFilter, setMovieFilter] = useState('')
  const movieFilterRef = useRef(null)
  const mySubmission = dcNominations[currentUserId]

  useEffect(() => {
    if (!currentActor?.id) return
    setLoading(true); setError('')
    getActorMovies(currentActor.id)
      .then((m) => setMovies(m))
      .catch(() => setError('Could not load movies'))
      .finally(() => setLoading(false))
  }, [currentActor?.id])

  // Local case-insensitive title filter — display only, no Firestore writes.
  const filteredMovies = movieFilter.trim()
    ? movies.filter(m => m.title.toLowerCase().includes(movieFilter.toLowerCase()))
    : movies

  if (mySubmission) {
    return (
      <div className="dc-gameplay dc-gameplay--submitted">
        <p className="dc-gameplay__headline">Pitched!</p>
        <div className="dc-gameplay__my-pick">
          {mySubmission.poster_path && (
            <img src={posterUrl(mySubmission.poster_path)} alt={mySubmission.title} />
          )}
          <p className="dc-gameplay__my-pick-name">{mySubmission.title}</p>
        </div>
        <p className="dc-gameplay__pulse">Waiting for other agents...</p>
      </div>
    )
  }

  return (
    <div className="dc-gameplay">
      <div className="dc-gameplay__header">
        <p className="dc-gameplay__title">Pitch your film!</p>
        <p className="dc-gameplay__sub">Pick a movie with <strong>{currentActor?.name}</strong></p>
      </div>
      {loading && <div className="dc-gameplay__loading"><div className="spinner" /></div>}
      {error && <p className="dc-gameplay__error">{error}</p>}
      {!loading && movies.length > 0 && (
        <div className="movie-filter-bar">
          <span className="movie-filter-bar__icon" aria-hidden="true">⌕</span>
          <input
            ref={movieFilterRef}
            className="movie-filter-bar__input"
            type="text"
            placeholder="Search movies…"
            value={movieFilter}
            onChange={e => setMovieFilter(e.target.value)}
            aria-label="Filter movies by title"
          />
          {movieFilter && (
            <>
              <span className="movie-filter-bar__count">
                {filteredMovies.length} / {movies.length}
              </span>
              <button
                type="button"
                className="movie-filter-bar__clear"
                onClick={() => { setMovieFilter(''); movieFilterRef.current?.focus() }}
                aria-label="Clear filter"
              ><IconX size={14} /></button>
            </>
          )}
        </div>
      )}
      {!loading && (
        <div className="dc-gameplay__grid dc-gameplay__grid--movies">
          {filteredMovies.map((m) => (
            <button
              key={m.id}
              type="button"
              className={`dc-gameplay__card${usedMovieIds.has(m.id) ? ' dc-gameplay__card--used' : ''}`}
              disabled={usedMovieIds.has(m.id)}
              onClick={async () => {
                await updateParty(party.id, {
                  [`dcNominations.${currentUserId}`]: {
                    type: 'movie',
                    id: m.id,
                    title: m.title,
                    poster_path: m.poster_path ?? null,
                  },
                }).catch(() => {})
              }}
            >
              <img src={m.poster_path ? posterUrl(m.poster_path) : MOVIE_PH} alt={m.title} />
              <span className="dc-gameplay__card-name">{m.title}</span>
            </button>
          ))}
        </div>
      )}
      {!loading && movies.length > 0 && filteredMovies.length === 0 && (
        <p className="movie-filter-bar__empty">
          No movies match "<strong>{movieFilter}</strong>"
        </p>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// ActorNominate — agent picks an actor from the chosen movie's cast
// ────────────────────────────────────────────────────────────────────────────
function ActorNominate({ party, currentUserId, currentMovie, goalActor, usedActorIds, currentActor, dcNominations }) {
  const [cast, setCast]       = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState('')
  const mySubmission = dcNominations[currentUserId]

  useEffect(() => {
    if (!currentMovie?.id) return
    setLoading(true); setError('')
    ;(async () => {
      try {
        let c = await getMovieCast(currentMovie.id)
        // Inject goal actor if missing (same pattern as Co-Op/H2H)
        if (goalActor && !c.some((a) => a.id === goalActor.id)) {
          try {
            const full = await getMovieCastFull(currentMovie.id)
            const entry = full.find((a) => a.id === goalActor.id)
            if (entry) c = [...c.slice(0, Math.max(0, c.length - 1)), entry]
          } catch {}
        }
        setCast(c)
      } catch { setError('Could not load cast') }
      finally { setLoading(false) }
    })()
  }, [currentMovie?.id, goalActor?.id])

  if (mySubmission) {
    return (
      <div className="dc-gameplay dc-gameplay--submitted">
        <p className="dc-gameplay__headline">Pitched!</p>
        <div className="dc-gameplay__my-pick">
          <img
            src={mySubmission.profile_path ? profileUrl(mySubmission.profile_path) : ACTOR_PH}
            alt={mySubmission.name}
          />
          <p className="dc-gameplay__my-pick-name">{mySubmission.name}</p>
        </div>
        <p className="dc-gameplay__pulse">Waiting for other agents...</p>
      </div>
    )
  }

  return (
    <div className="dc-gameplay">
      <div className="dc-gameplay__header">
        <p className="dc-gameplay__title">Pitch your actor!</p>
        <p className="dc-gameplay__sub">Pick someone from <strong>{currentMovie?.title}</strong></p>
        <p className="dc-gameplay__goal">Goal: <strong>{goalActor?.name}</strong></p>
      </div>
      {loading && <div className="dc-gameplay__loading"><div className="spinner" /></div>}
      {error && <p className="dc-gameplay__error">{error}</p>}
      {!loading && (
        <div className="dc-gameplay__grid">
          {cast.map((a) => {
            const isUsed = usedActorIds.has(a.id) || a.id === currentActor?.id
            const isGoal = a.id === goalActor?.id
            return (
              <button
                key={a.id}
                type="button"
                className={[
                  'dc-gameplay__card',
                  isUsed ? 'dc-gameplay__card--used' : '',
                  isGoal ? 'dc-gameplay__card--goal' : '',
                ].join(' ').trim()}
                disabled={isUsed}
                onClick={async () => {
                  await updateParty(party.id, {
                    [`dcNominations.${currentUserId}`]: {
                      type: 'actor',
                      id: a.id,
                      name: a.name,
                      profile_path: a.profile_path ?? null,
                    },
                  }).catch(() => {})
                }}
              >
                <img src={a.profile_path ? profileUrl(a.profile_path) : ACTOR_PH} alt={a.name} />
                <span className="dc-gameplay__card-name">{a.name}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// WaitingForSubmissions — Director / Host view while agents pitch
// ────────────────────────────────────────────────────────────────────────────
function WaitingForSubmissions({ party, role, agentUids, nominations, title }) {
  const players = party?.players ?? {}
  return (
    <div className="dc-gameplay dc-gameplay--waiting">
      {role === 'director' && <DCDirectorBanner />}
      <h2 className="dc-gameplay__title">{title}</h2>
      {role === 'host' && <DCHostDirectorLine party={party} />}
      <p className="dc-gameplay__sub">
        {Object.keys(nominations).length}/{agentUids.length} submitted
      </p>
      <div className="dc-casting__avatars">
        {agentUids.map(uid => {
          const p = players[uid]
          const done = !!nominations[uid]
          const initial = (p?.username || '?').charAt(0).toUpperCase()
          return (
            <div key={uid} className="dc-casting__avatar-chip">
              <div className="dc-casting__avatar">
                {p?.avatar
                  ? <img src={p.avatar} alt="" />
                  : <span className="party-avatar__initial">{initial}</span>}
              </div>
              <span className="dc-casting__avatar-name">
                {done
                  ? <span className="party-ready-check" aria-label="Submitted"><IconCheck size={12} /></span>
                  : <span className="party-ready-dot" aria-label="Pitching" />}
                <span>{p?.username || 'Guest'}</span>
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// DirectorPick — anonymous grid for Director (Host watches the same view)
// ────────────────────────────────────────────────────────────────────────────
function DirectorPick({ party, role, kind, nominations, currentActor, goalActor }) {
  const isDirector = role === 'director'
  const isHost     = role === 'host'

  // Fix 5: dedupe by item id; track all nominators per unique item.
  const groups = (() => {
    const m = new Map()
    for (const [uid, n] of Object.entries(nominations)) {
      const key = `${n.type}:${n.id}`
      const existing = m.get(key)
      if (existing) existing.nominatorUids.push(uid)
      else m.set(key, { nomination: n, nominatorUids: [uid] })
    }
    return Array.from(m.values())
  })()

  // Fix 3: per-card flip state + lazy fetch of cast / movies.
  const [flippedId, setFlippedId]   = useState(null)
  const [flipData, setFlipData]     = useState({})
  const [flipLoading, setFlipLoading] = useState({})

  const handleCardTap = async (group) => {
    if (!isDirector) return
    const id = group.nomination.id
    if (flippedId === id) { setFlippedId(null); return }
    setFlippedId(id)
    if (!flipData[id] && !flipLoading[id]) {
      setFlipLoading((s) => ({ ...s, [id]: true }))
      try {
        const data = group.nomination.type === 'movie'
          ? (await getMovieCast(id)).slice(0, 8)
          : (await getActorMovies(id)).slice(0, 8)
        setFlipData((s) => ({ ...s, [id]: data }))
      } catch {
        setFlipData((s) => ({ ...s, [id]: [] }))
      } finally {
        setFlipLoading((s) => ({ ...s, [id]: false }))
      }
    }
  }

  const handlePick = async (group) => {
    if (!isDirector || !party?.id) return
    const { nomination: nom, nominatorUids } = group

    // Fix 4: if picking a MOVIE and the goal actor is in its cast,
    // skip the actor-nomination round and end the game immediately.
    let autoCompleteWinner = null
    if (nom.type === 'movie' && goalActor?.id) {
      try {
        const cast = await getMovieCast(nom.id)
        const hit = cast.find((c) => c.id === goalActor.id)
        if (hit) autoCompleteWinner = hit
        else {
          try {
            const full = await getMovieCastFull(nom.id)
            autoCompleteWinner = full.find((c) => c.id === goalActor.id) || null
          } catch { autoCompleteWinner = null }
        }
      } catch { autoCompleteWinner = null }
    }

    // Auto-complete grants +2 for movie nominators; normal pick is +1.
    const points = { ...(party.agentPoints || {}) }
    const award = autoCompleteWinner ? 2 : 1
    for (const u of nominatorUids) {
      points[u] = (points[u] || 0) + award
    }

    const movieItem = nom.type === 'movie'
      ? { type: 'movie', id: nom.id, title: nom.title, poster_path: nom.poster_path ?? null, nominators: nominatorUids }
      : null
    const actorItem = nom.type === 'actor'
      ? { type: 'actor', id: nom.id, name: nom.name, profile_path: nom.profile_path ?? null, nominators: nominatorUids }
      : null

    const prevChain   = Array.isArray(party.dcChain) ? party.dcChain : []
    const prevHistory = Array.isArray(party.dcRoundHistory) ? party.dcRoundHistory : []

    if (autoCompleteWinner) {
      const goalChainItem = {
        type: 'actor',
        id: goalActor.id,
        name: goalActor.name,
        profile_path: goalActor.profile_path ?? null,
        nominators: [],
        autoCompleted: true,
      }
      const dcChain = [...prevChain, movieItem, goalChainItem]
      const dcRoundHistory = [
        ...prevHistory,
        { kind: 'movie', pickedId: nom.id, pickedLabel: nom.title, nominators: nominatorUids, autoCompleted: true },
      ]
      await updateParty(party.id, {
        dcChain,
        dcRoundHistory,
        agentPoints: points,
        dcLastPickedLabel: `${nom.title} (Goal reached!)`,
        dcLastScoredUids: nominatorUids,
        status: 'dc-results',
        dcStep: 'done',
        dcStepStartedAt: new Date(),
        finishedAt: new Date(),
        solvedAt: new Date(),
      }).catch(() => {})
      return
    }

    const chainItem = nom.type === 'movie' ? movieItem : actorItem
    const dcChain = [...prevChain, chainItem]
    const dcRoundHistory = [...prevHistory, {
      kind: nom.type,
      pickedId: nom.id,
      pickedLabel: nom.title || nom.name,
      nominators: nominatorUids,
      nominations: groups.map(g => ({ nominatorUids: g.nominatorUids, ...g.nomination })),
    }]
    await updateParty(party.id, {
      dcChain,
      dcRoundHistory,
      agentPoints: points,
      dcLastPickedLabel: nom.title || nom.name,
      dcLastScoredUids: nominatorUids,
      dcStep: nom.type === 'movie' ? 'movie-score' : 'actor-score',
      dcStepStartedAt: new Date(),
    }).catch(() => {})
  }

  return (
    <div className="dc-gameplay dc-gameplay--pick">
      {isDirector && <DCDirectorBanner />}
      <h2 className="dc-gameplay__title">
        {isDirector
          ? `Pick the ${kind === 'movie' ? 'film' : 'actor'}`
          : role === 'agent'
          ? 'The Director is reviewing the submissions...'
          : `The Director is choosing the ${kind}`}
      </h2>
      {isHost && <DCHostDirectorLine party={party} />}
      <p className="dc-gameplay__sub">
        {isDirector ? 'Tap a card to flip and inspect. Tap “Pick this” to commit.' : 'Anonymous — no titles, no agents.'}
      </p>
      <div className="dc-casting__pool">
        {groups.map((group) => {
          const n = group.nomination
          const id = n.id
          const isFlipped = flippedId === id
          const isLoading = !!flipLoading[id]
          const flipped = flipData[id]
          return (
            <div
              key={`${n.type}:${id}`}
              className={`dc-pick-card${isFlipped ? ' dc-pick-card--flipped' : ''}`}
            >
              <button
                type="button"
                className="dc-pick-card__face dc-pick-card__face--front"
                disabled={!isDirector}
                onClick={() => handleCardTap(group)}
                title={isDirector && group.nominatorUids.length > 1 ? `${group.nominatorUids.length} agents nominated this` : undefined}
              >
                <img
                  src={
                    n.type === 'movie'
                      ? (n.poster_path ? posterUrl(n.poster_path) : MOVIE_PH)
                      : (n.profile_path ? profileUrl(n.profile_path) : ACTOR_PH)
                  }
                  alt=""
                />
              </button>
              {isFlipped && (
                <div className="dc-pick-card__face dc-pick-card__face--back">
                  <p className="dc-pick-card__back-label">
                    {n.type === 'movie' ? 'Top cast' : 'Top films'}
                  </p>
                  {isLoading && <div className="spinner spinner--sm" />}
                  {!isLoading && flipped && flipped.length > 0 && (
                    <ul className="dc-pick-card__list">
                      {flipped.map((item) => (
                        <li key={item.id}>{n.type === 'movie' ? item.name : item.title}</li>
                      ))}
                    </ul>
                  )}
                  {!isLoading && flipped && flipped.length === 0 && (
                    <p className="dc-pick-card__list-empty">No info available.</p>
                  )}
                  <div className="dc-pick-card__actions">
                    <button type="button" className="btn-primary dc-pick-card__pick-this" onClick={() => handlePick(group)}>
                      Pick this
                    </button>
                    <button type="button" className="btn-ghost" onClick={() => setFlippedId(null)}>
                      Back
                    </button>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
